import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import './Checkbox.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Visible label associated with the checkbox via htmlFor/id. Usually a
   * string; may contain inline links (e.g. "I accept the <a>terms</a>").
   */
  label: ReactNode;
  /** Optional id override; if not provided, one is generated via React's useId() */
  id?: string;
  /** Optional class name applied to the outer wrapper element */
  className?: string;
  /** Optional class name applied to the <input> element itself */
  inputClassName?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, id, className, inputClassName, ...rest }, ref) => {
    const generatedId = useId();
    const resolvedId = id ?? `rg-checkbox-${generatedId}`;
    const rootClassName = ['rg-checkbox', className].filter(Boolean).join(' ');
    const inputClassNameResolved = ['rg-checkbox__input', inputClassName].filter(Boolean).join(' ');

    return (
      <div className={rootClassName}>
        <input
          ref={ref}
          type="checkbox"
          id={resolvedId}
          className={inputClassNameResolved}
          {...rest}
        />
        <label htmlFor={resolvedId} className="rg-checkbox__label">
          {label}
        </label>
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
